"use client"
import {
  Button,
  Container,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material"
import { DelekReplacementsResponse, GetPriceListReplacements } from "api/delek"
import BreadCrumbsWrapper from "common/BreadCrumbs"
import { useIsDesktop } from "common/MediaQueries"
import NextLink from "common/bit/NextLink"
import FormikForm from "common/bit/form/FormikForm"
import FormikInput from "common/bit/form/FormikInput"
import { schemaCommon } from "common/bit/form/FormikSchema"
import { ToggleButtons } from "components/mapPages/map/utils/mapFilters"
import { useState } from "react"
import TwoToggleButtons from "reusable/TwoToggleButtons"
import {
  StyledBody,
  StyledHeaderH2,
  StyledLi,
  StyledLink,
  StyledUl,
} from "reusable/wrappers/BodyText"
import ImageContainer from "reusable/wrappers/ImageContainer"
import styled from "styled-components"
import { StyledPageWrapper, TypographyRtl } from "theme/shared"
import * as yup from "yup"
import SparePriceListData from "./SparePriceListData"

const StyledPageDiv = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  display: flex;
  align-items: center;
  background: ${(p) => p.theme.colors.lightGrey};
  padding-bottom: 80px;
  .MuiOutlinedInput-root {
    width: 320px;
    ${(p) => p.theme.breakpoints.down("sm")} {
      width: 100%;
    }
  }
`

const StyledTopWrapper = styled.div`
  text-align: center;
  margin-top: 40px;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  ${(p) => p.theme.breakpoints.down("sm")} {
    text-align: right;
    align-items: flex-start;
  }
`
const StyledToggleButtons = styled(TwoToggleButtons)`
  margin-top: 50px;
  ${(p) => p.theme.breakpoints.down("sm")} {
    margin-top: 20px;
    width: 100%;
  }
`

const StyledWrap = styled.div`
  ${(p) => p.theme.breakpoints.up("md")} {
    max-width: 320px;
  }
  ${(p) => p.theme.breakpoints.down("sm")} {
    width: 100%;
  }
`

const StyledButton = styled(Button)`
  margin: 20px 0px;
`

const StyledLinkWrapp = styled(NextLink)`
  color: ${(p) => p.theme.colors.saphire};
  font-size: 18px;
  text-decoration: underline;
  margin-top: 40px;
  ${(p) => p.theme.breakpoints.up("md")} {
    margin-bottom: 40px;
  }
`

const StyledLine = styled.div`
  background: ${(p) => p.theme.colors.darkGrey};
  height: 1px;
  width: 100%;
`
const StyledContainer = styled(Container)``

const StyledBodyWrapp = styled.div`
  margin-top: 40px;
  ${(p) => p.theme.breakpoints.down("sm")} {
    margin-top: 0px;
  }
  li {
    font-size: 18px;
    weight: 400;
  }
`
const StyledError = styled(Typography).attrs({ variant: "caption" })`
  font-size: 12px;
  color: ${(p) => p.theme.colors.mazdaRed};
  position: absolute;
  width: 320px;
  bottom: -15px;
  right: 0;
  margin-top: 10px;
`

const StyledFormWrapper = styled.div<{ $error: boolean }>`
  position: relative;
  margin-top: 25px;
  fieldset {
    border-color: ${(p) => p.$error && "#b50303 !important"};
  }
`

const schemaDescription = yup
  .object({
    description: schemaCommon.catalog,
  })
  .defined()

const schemaCatalog = yup
  .object({
    catalogNumber: schemaCommon.catalog,
  })
  .defined()

const SparePriceListPage = () => {
  const isDesktop = useIsDesktop()
  const [toggleIndex, setToggleIndex] = useState(ToggleButtons.CLOSE_TO_ME)
  const catalogValue = { catalogNumber: "", description: "" }
  const [response, setResponse] = useState<DelekReplacementsResponse | null>(
    null
  )
  const [skuError, setSkuError] = useState<string | null>(null)
  const [nameError, setNameError] = useState<string | null>(null)
  const [openTable, setOpenTable] = useState(false)
  const error =
    "אנא בדוק שהזנת את הפרטים הנכונים ונסה שוב.  במידה ועדיין לא מצאת את מה שאתה מחפש הנך מוזמן ליצור קשר עם מוקד השירות."

  const date = new Date()
  const firstDayOfMonth = new Date(date.getFullYear(), date.getMonth(), 1)
  const formattedDate = firstDayOfMonth
    .toLocaleDateString("he-IL", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    })
    .replace(/(\d{2})\/(\d{2})\/(\d{4})/, "$1.$2.$3")
  return (
    <StyledPageWrapper $padding="0">
      <StyledPageDiv>
        <ImageContainer
          src={isDesktop ? "/img/spareprice.jpg" : "/img/sparepricemobile.jpg"}
        />
        <BreadCrumbsWrapper name="SparePriceList" />
        <StyledContainer>
          <StyledTopWrapper>
            <TypographyRtl variant="h1" $fontSize="24px" $fontWeight="400">
              מחירון מוצרי תעבורה (חלפים){" "}
            </TypographyRtl>
            <StyledBody $margin={20}>
              לחיפוש, יש להקליד כאן את המספר הקטלוגי או את שם חלק מתיאור הפריט
            </StyledBody>
            <StyledToggleButtons
              activeButton={toggleIndex}
              setActiveButton={setToggleIndex}
              optionsList={["מס׳ קטלוגי", "שם פריט"]}
              toggleId="map-toggle"
            />
            <FormikForm
              validationSchema={
                toggleIndex === 0 ? schemaCatalog : schemaDescription
              }
              initialValues={catalogValue}
              onSubmit={async (values) => {
                const { data } = await GetPriceListReplacements(
                  toggleIndex === 0 ? values.catalogNumber : values.description,
                  toggleIndex === 0 ? "sku" : "name"
                )
                setResponse(data)
                if (data.data.length === 0) {
                  toggleIndex === 0 ? setSkuError(error) : setNameError(error)
                } else {
                  setSkuError(null)
                  setNameError(null)
                }
              }}
              render={(formik) => (
                <>
                  <StyledFormWrapper
                    $error={
                      !!(
                        (skuError && toggleIndex === 0) ||
                        (nameError && toggleIndex === 1)
                      )
                    }
                  >
                    <FormikInput
                      name={`${
                        toggleIndex === 0 ? "catalogNumber" : "description"
                      }`}
                      label={
                        toggleIndex === 0
                          ? "L3C115909 :מק״ט, לדוגמה"
                          : "שם פריט, לדוגמה: מסנן שמן"
                      }
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        const btnType =
                          toggleIndex === 0 ? "catalogNumber" : "description"
                        formik.setFieldValue(btnType, e.target.value)
                        if (
                          btnType === "catalogNumber" &&
                          skuError &&
                          e.target.value.length === 0
                        ) {
                          setSkuError(null)
                        }
                        if (
                          btnType === "description" &&
                          nameError &&
                          e.target.value.length === 0
                        ) {
                          setNameError(null)
                        }
                      }}
                    />
                    {skuError &&
                      toggleIndex === 0 &&
                      formik.values.catalogNumber.length > 0 && (
                        <StyledError id="catalogNumber-helper-text">
                          {skuError}
                        </StyledError>
                      )}
                    {nameError &&
                      toggleIndex === 1 &&
                      formik.values.description.length > 0 && (
                        <StyledError id="description-helper-text">
                          {nameError}
                        </StyledError>
                      )}
                  </StyledFormWrapper>
                  <StyledWrap>
                    <StyledButton
                      disabled={formik.isSubmitting}
                      fullWidth
                      variant="text"
                      type={"submit"}
                    >
                      לחיפוש
                    </StyledButton>
                  </StyledWrap>
                </>
              )}
            />
            {response?.data && response.data.length > 0 && (
              <SparePriceListData data={response.data} />
            )}
            <StyledLinkWrapp href="/services-centers">
              לרשימת מרכזי שירות מורשים
            </StyledLinkWrapp>
            {isDesktop && <StyledLine></StyledLine>}
          </StyledTopWrapper>
          <StyledBodyWrapp>
            {isDesktop && (
              <>
                <StyledHeaderH2>מוקד שירות לקוחות</StyledHeaderH2>
                <StyledLink
                  href={"tel:*2880"}
                  $color="#4C607E"
                  style={{ unicodeBidi: "plaintext" }}
                >
                  {" "}
                  *2880
                </StyledLink>
              </>
            )}
            <StyledUl $padding={25}>
              <StyledLi $margin={20}>המחיר לעיל כולל מע"מ.</StyledLi>
              <StyledLi $margin={5}>
                המחירים אינם כוללים את עבודת הפירוק וההתקנה ברכב
              </StyledLi>
              <StyledLi $margin={5}>
                הנתונים המוצגים במנגנון זה נכונים לתאריך {formattedDate}
              </StyledLi>
              <StyledLi $margin={5}>
                דלק מוטורס רשאית לשנות את מחירי החלפים בכל עת וללא הודעה מוקדמת
              </StyledLi>
              <StyledLi $margin={5}>
                אנו פועלים לעדכון שוטף של המחירים במנגנון זה אולם יתכנו הבדלים
                בין המחירים המופיעים בו לבין מחירי החלפים בפועל
              </StyledLi>
              <StyledLi $margin={5}>
                אחריות לחלפים מקוריים: האחריות לחלק תינתן למשך 12 חודשים ללא
                הגבלת ק"מ ממועד התקנתו ברכב
              </StyledLi>
              <StyledLi $margin={5}>
                האחריות הינה לחלק בלבד והיא לא תכסה את העבודה ו/או פגם שנוצר עקב
                התקנה לקויה ו/או שימוש בלתי סביר ברכב
              </StyledLi>
              <StyledLi $margin={5}>
                אחריות לחלפים שאינם מקוריים: האחריות לחלק שאינו מקורי תינתן למשך
                3 חודשים או 6000 ק"מ ממועד התקנתם ברכב, המוקדם מבניהם, למעט
                חלפים המפורטים ברשימה המצורפת{" "}
                <StyledLink
                  href=""
                  $color="#4C607E"
                  onClick={(e) => {
                    e?.preventDefault()
                    setOpenTable((isOpen) => !isOpen)
                  }}
                >
                  לחץ כאן
                </StyledLink>
                {openTable && (
                  <TableContainer
                    component={Paper}
                    style={{ margin: "16px 0" }}
                  >
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>סוג הפריט</TableCell>
                          <TableCell>האחריות הניתנת לגביו</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow>
                          <TableCell>מצברים לא מקוריים</TableCell>
                          <TableCell>
                            אחריות בהתאם לזו המסופקת ע"י יצרן המצברים (12 חודשים
                            או 20 חודשים בהתאם לסוג המצבר שנרכש).
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>תיבות הילוכים משופצות</TableCell>
                          <TableCell>
                            אחריות של 12 חודשים או 20,000 ק"מ המוקדם שבהם
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </StyledLi>
              <StyledLi $margin={5}>
                לדגמי רכב רבים קיימים תתי דגמים ולעיתים נדרשות בקיאות והתמצאות
                באיתור המק"ט של החלק המתאים לדגם הרכב הרצוי. אנו ממליצים להיכנס
                למרכז שירות מורשה מטעם דלק מוטורס לזיהוי
              </StyledLi>
              <StyledLi $margin={5}>
                דלק מוטורס בע"מ ת.ד. 200 מושב ניר צבי 72905
              </StyledLi>
            </StyledUl>
            {!isDesktop && (
              <>
                <StyledHeaderH2 $margin={40}>מוקד שירות לקוחות</StyledHeaderH2>
                <StyledLink
                  href={"tel:*2880"}
                  $color="#4C607E"
                  style={{ unicodeBidi: "plaintext" }}
                >
                  {" "}
                  *2880
                </StyledLink>
              </>
            )}
          </StyledBodyWrapp>
        </StyledContainer>
      </StyledPageDiv>
    </StyledPageWrapper>
  )
}

export default SparePriceListPage
